import { nanoid } from '@reduxjs/toolkit';
import Notification from '@rio-cloud/rio-uikit/components/notification/Notification';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { ENudgeAction } from '@/components/molecule/FeedbackNudge/types';
import { getUserProfile } from '@/configuration/login/loginSlice';
import { FEEDBACK_NUDGE_ACTIONS, FEEDBACK_NUDGE_CATEGORY, gaPush } from '@/configuration/setup/googleAnalytics';
import { useAppSelector } from '@/configuration/setup/hooks';
import { EAppRoutes } from '@/routes/routes';
import { useSubmitFeedbackMutation } from '@/services/feedbackApi/feedbackApi';
import { TMessage } from '@/store/chat/types';
import { selectActiveThreadId } from '@/store/thread/threadSlice';
import { getUserId } from '@/utils/getUserId';

const FEEDBACK_NUDGE_DISMISSED_KEY = 'feedbackNudgeDismissed';

/**
 * Manages the feedback nudge lifecycle: detection, display, dismissal, and GA tracking.
 * Shows the nudge after the 2nd AI response in a session. Persists dismissal in sessionStorage.
 */
export const useFeedbackNudge = (messages: TMessage[] | undefined) => {
  const intl = useIntl();
  const activeThreadId = useAppSelector(selectActiveThreadId);
  const user = useAppSelector(getUserProfile);
  const userId = getUserId(user?.sub ?? '');
  const [submitFeedback] = useSubmitFeedbackMutation();

  const [showNudge, setShowNudge] = useState(false);
  const aiResponseCountRef = useRef(0);
  const isDismissedRef = useRef(sessionStorage.getItem(FEEDBACK_NUDGE_DISMISSED_KEY) === 'true');
  const prevMessageCountRef = useRef(messages?.length);

  useEffect(() => {
    const prevCount = prevMessageCountRef.current ?? 0;
    const currentCount = messages?.length ?? 0;
    prevMessageCountRef.current = currentCount;

    if (currentCount !== prevCount + 1 || !messages) return;

    const latestMessage = messages[currentCount - 1];

    if (showNudge && latestMessage?.role === 'human') {
      setShowNudge(false);
      isDismissedRef.current = true;
      sessionStorage.setItem(FEEDBACK_NUDGE_DISMISSED_KEY, 'true');
      gaPush({ category: FEEDBACK_NUDGE_CATEGORY, action: FEEDBACK_NUDGE_ACTIONS.DISMISSED, label: '' });
      return;
    }

    if (latestMessage?.role === 'ai' && !latestMessage?.isOnboardingQuestion) {
      aiResponseCountRef.current += 1;
      if (aiResponseCountRef.current >= 2 && !isDismissedRef.current) {
        setShowNudge(true);
      }
    }
  }, [messages?.length]); // INFO: messages?.length: detects when a new message is added to the active thread

  useEffect(() => {
    if (showNudge) {
      gaPush({ category: FEEDBACK_NUDGE_CATEGORY, action: FEEDBACK_NUDGE_ACTIONS.SHOWN, label: '' });
    }
  }, [showNudge]); // INFO: showNudge: fires GA event once when nudge becomes visible

  const dismissNudge = useCallback((action: string) => {
    isDismissedRef.current = true;
    sessionStorage.setItem(FEEDBACK_NUDGE_DISMISSED_KEY, 'true');
    setShowNudge(false);
    gaPush({ category: FEEDBACK_NUDGE_CATEGORY, action, label: '' });
  }, []);

  const handleNudgeAction = useCallback(
    async (action: ENudgeAction) => {
      const detailAsThreadId = `Overall experience feedback from thread ID: ${activeThreadId}`;

      switch (action) {
        case ENudgeAction.LIKE:
          dismissNudge(FEEDBACK_NUDGE_ACTIONS.LIKE);
          await submitFeedback({ id: nanoid(), rate: 5, userId, detail: detailAsThreadId });
          Notification.success(intl.formatMessage({ id: 'chatPage.feedbackNudge.success.notification' }));
          break;
        case ENudgeAction.DISLIKE:
          dismissNudge(FEEDBACK_NUDGE_ACTIONS.DISLIKE);
          await submitFeedback({ id: nanoid(), rate: 1, userId, detail: detailAsThreadId });
          Notification.success(intl.formatMessage({ id: 'chatPage.feedbackNudge.success.notification' }));
          break;
        case ENudgeAction.COMMENT:
          dismissNudge(FEEDBACK_NUDGE_ACTIONS.COMMENT);
          window.open(EAppRoutes.FEEDBACK, '_blank');
          break;
      }
    },
    [userId, submitFeedback, dismissNudge, intl, activeThreadId],
  );

  const handleNudgeClose = useCallback(() => {
    dismissNudge(FEEDBACK_NUDGE_ACTIONS.DISMISSED);
  }, [dismissNudge]);

  return { showNudge, dismissNudge, handleNudgeAction, handleNudgeClose };
};
